import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  useTheme,
  CircularProgress,
  IconButton,
  Collapse,
} from "@mui/material";
import { ClockifyTimeEntry } from "../../features/clockifyTimeEntries/ClockifyTimeEntry";
import TimeFormatter from "../../features/extensions/TimeFormatter";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ExportToExcelButton from "../exportToExcelButton/ExportToExcelButton";
import { TypeOfWorkSummaryExcelExporter } from "../../features/typeOfWorkSummary/TypeOfWorkSummaryExcelExporter";
import { useState } from "react";
import { Milestone } from "../../features/projects/Milestone";

interface TypeOfWorkSummaryProps {
  timeEntries: ClockifyTimeEntry[];
  selectedMilestone?: Milestone;
  loading?: boolean;
  isExpanded?: boolean;
  showExpandButton?: boolean;
  onExpandClick?: () => void;
}

interface TypeOfWorkSummary {
  typeOfWork: string;
  totalHours: number;
}

const TypeOfWorkSummaryTable = ({
  timeEntries,
  selectedMilestone,
  loading = false,
  isExpanded = true,
  showExpandButton = false,
  onExpandClick,
}: TypeOfWorkSummaryProps) => {
  const theme = useTheme();
  const timeFormatter = new TimeFormatter();
  const [isExportToExcelLoading, setIsExportToExcelLoading] = useState(false);

  const typeOfWorkSummaryExcelExporter = new TypeOfWorkSummaryExcelExporter();

  const handleExportToExcel = async () => {
    setIsExportToExcelLoading(true);
    try {
      typeOfWorkSummaryExcelExporter.exportToExcel(
        timeEntries,
        `Type of Work Summary - ${selectedMilestone?.name || "Unknown"}`
      );
    } catch (error) {
      console.error("Error exporting to Excel:", error);
    } finally {
      setIsExportToExcelLoading(false);
    }
  };

  const aggregateTypeOfWorkData = (): TypeOfWorkSummary[] => {
    const typeOfWorkMap = new Map<string, number>();

    timeEntries.forEach((entry) => {
      const typeOfWork = entry.typeOfWork || "N/A";
      const currentTotal = typeOfWorkMap.get(typeOfWork) || 0;
      typeOfWorkMap.set(typeOfWork, currentTotal + entry.timeSpentInHours);
    });

    return Array.from(typeOfWorkMap.entries())
      .map(([typeOfWork, totalHours]) => ({
        typeOfWork,
        totalHours,
      }))
      .sort((a, b) => {
        if (a.typeOfWork === "N/A" && b.typeOfWork !== "N/A") return 1;
        if (a.typeOfWork !== "N/A" && b.typeOfWork === "N/A") return -1;
        return b.totalHours - a.totalHours;
      });
  };

  const typeOfWorkData = aggregateTypeOfWorkData();
  const hasData = typeOfWorkData.length > 0;
  const hasValidTypeOfWork = typeOfWorkData.some(
    (item) => item.typeOfWork && item.typeOfWork !== "N/A"
  );

  // Don't show the table if there's no valid type of work
  if (!loading && !hasValidTypeOfWork) {
    return null;
  }

  if (loading && !hasData) {
    return (
      <Box sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            px: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {showExpandButton && (
              <IconButton onClick={onExpandClick} size="small" sx={{ mr: 1 }}>
                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
            <Typography variant="h6" component="h2">
              Types of Work
            </Typography>
          </Box>
          <ExportToExcelButton
            isLoading={isExportToExcelLoading}
            onClick={handleExportToExcel}
          />
        </Box>
        <Collapse in={isExpanded}>
          <Paper elevation={3} sx={{ mb: 3, overflow: "hidden" }}>
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          </Paper>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          px: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {showExpandButton && (
            <IconButton onClick={onExpandClick} size="small" sx={{ mr: 1 }}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
          <Typography variant="h6" component="h2">
            Types of Work
          </Typography>
        </Box>
        <ExportToExcelButton
          isLoading={isExportToExcelLoading}
          onClick={handleExportToExcel}
        />
      </Box>

      <Collapse in={isExpanded}>
        <Paper elevation={3} sx={{ mb: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table aria-label="types of work summary table">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      fontWeight: 600,
                      width: "60%",
                    }}
                  >
                    Type of Work
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      fontWeight: 600,
                      width: "40%",
                    }}
                  >
                    Total Hours
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!hasData ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  typeOfWorkData.map((item, index) => (
                    <TableRow
                      key={`${item.typeOfWork}-${index}`}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        <Typography
                          sx={{
                            fontWeight: item.typeOfWork === "N/A" ? 400 : 500,
                            color:
                              item.typeOfWork === "N/A"
                                ? "text.secondary"
                                : "text.primary",
                            fontStyle:
                              item.typeOfWork === "N/A" ? "italic" : "normal",
                          }}
                        >
                          {item.typeOfWork}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontFamily: "monospace",
                            color:
                              item.typeOfWork === "N/A"
                                ? "text.secondary"
                                : "text.primary",
                          }}
                        >
                          {timeFormatter.toHoursAndMinutes(item.totalHours)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default TypeOfWorkSummaryTable;
